/**
 * 처방 하이라이트 캔버스 — 레거시 HighlightCanvas.js 포팅 (antd 제거).
 */
import {
  drawRowHighlights,
  isPointInsideRowBounds,
} from '@/shared/lib/highlighter/highlightDrawing';
import {
  makeBandFromDrag,
  pointInPolygon,
} from '@/shared/lib/highlighter/manualBands';
import type { HighlightGeometryRow } from '@/shared/lib/highlighter/computeHighlightGeometry';
import React, { useEffect, useMemo, useRef, useState } from 'react';

export type HighlighterTool = 'select' | 'edit' | 'draw';

export interface ManualBand {
  id: string;
  vertices: Array<{ x: number; y: number }>;
  color?: string;
}

export interface SelectedBand {
  type: 'manual' | 'ocr';
  id: string;
}

interface HighlightCanvasProps {
  imageAsset: { previewUrl: string; name: string } | null;
  rows: HighlightGeometryRow[];
  selectedRowIds: string[];
  companyColors: Record<string, string>;
  onToggleRow: (rowId: string) => void;
  tool?: HighlighterTool;
  manualBands?: ManualBand[];
  thickness?: number;
  selectedBand?: SelectedBand | null;
  onSelectBand?: (band: SelectedBand | null) => void;
  onMoveBand?: (band: SelectedBand, dx: number, dy: number) => void;
  onDrawBand?: (vertices: Array<{ x: number; y: number }>) => void;
  onDeleteSelected?: () => void;
}

const syncCanvasToImage = (
  canvas: HTMLCanvasElement,
  image: HTMLImageElement,
) => {
  const width = image.clientWidth;
  const height = image.clientHeight;
  if (!width || !height) return null;
  canvas.width = width;
  canvas.height = height;
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;
  return { width, height };
};

const hexToRgba = (hexColor: string, alpha: number) => {
  const v = (hexColor || '#fa541c').replace('#', '');
  const n =
    v.length === 3
      ? v
          .split('')
          .map((c) => c + c)
          .join('')
      : v;
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const tracePath = (
  ctx: CanvasRenderingContext2D,
  vertices: Array<{ x: number; y: number }>,
  width: number,
  height: number,
) => {
  ctx.beginPath();
  ctx.moveTo(vertices[0].x * width, vertices[0].y * height);
  for (let i = 1; i < vertices.length; i += 1) {
    ctx.lineTo(vertices[i].x * width, vertices[i].y * height);
  }
  ctx.closePath();
};

const firstPolygon = (row: HighlightGeometryRow | undefined) => {
  if (Array.isArray(row?.normalizedPolygons) && row.normalizedPolygons.length) {
    return row.normalizedPolygons.find((p) => p?.length >= 3) || null;
  }
  if (row?.normalizedVertices && row.normalizedVertices.length >= 3) {
    return row.normalizedVertices;
  }
  return null;
};

const MANUAL_COLOR = '#fa541c';

export const HighlightCanvas: React.FC<HighlightCanvasProps> = ({
  imageAsset,
  rows,
  selectedRowIds,
  companyColors,
  onToggleRow,
  tool = 'select',
  manualBands = [],
  thickness = 0.022,
  selectedBand = null,
  onSelectBand,
  onMoveBand,
  onDrawBand,
  onDeleteSelected,
}) => {
  const imageRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const dragRef = useRef<{
    mode: 'move' | 'draw';
    last?: { x: number; y: number };
    start?: { x: number; y: number };
    band?: SelectedBand;
  } | null>(null);
  const [ready, setReady] = useState(false);
  const [drawPreview, setDrawPreview] = useState<Array<{
    x: number;
    y: number;
  }> | null>(null);

  const selectedRowIdSet = useMemo(
    () => new Set(selectedRowIds),
    [selectedRowIds],
  );

  const selectedVertices = useMemo(() => {
    if (!selectedBand) return null;
    if (selectedBand.type === 'manual') {
      return (
        manualBands.find((b) => b.id === selectedBand.id)?.vertices || null
      );
    }
    const row = rows.find((r) => r.rowId === selectedBand.id);
    return firstPolygon(row);
  }, [selectedBand, manualBands, rows]);

  useEffect(() => {
    const imageElement = imageRef.current;
    const canvasElement = canvasRef.current;
    if (!imageElement || !canvasElement) return undefined;

    const redraw = () => {
      const size = syncCanvasToImage(canvasElement, imageElement);
      if (!size) return;
      const ctx = canvasElement.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, canvasElement.width, canvasElement.height);

      drawRowHighlights({
        context: ctx,
        rows,
        selectedRowIds: selectedRowIdSet,
        companyColors,
        width: size.width,
        height: size.height,
      });

      manualBands.forEach((band) => {
        if (!band.vertices || band.vertices.length < 3) return;
        tracePath(ctx, band.vertices, size.width, size.height);
        ctx.setLineDash([]);
        ctx.fillStyle = hexToRgba(band.color || MANUAL_COLOR, 0.16);
        ctx.fill();
        ctx.strokeStyle = hexToRgba(band.color || MANUAL_COLOR, 0.55);
        ctx.lineWidth = 1;
        ctx.stroke();
      });

      if (drawPreview) {
        tracePath(ctx, drawPreview, size.width, size.height);
        ctx.setLineDash([6, 4]);
        ctx.fillStyle = hexToRgba(MANUAL_COLOR, 0.12);
        ctx.fill();
        ctx.strokeStyle = hexToRgba(MANUAL_COLOR, 0.9);
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.setLineDash([]);
      }

      if (selectedVertices && selectedVertices.length >= 3) {
        tracePath(ctx, selectedVertices, size.width, size.height);
        ctx.setLineDash([4, 3]);
        ctx.strokeStyle = '#1677ff';
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.setLineDash([]);
      }
    };

    redraw();
    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = new ResizeObserver(() => redraw());
    resizeObserverRef.current.observe(imageElement);
    return () => resizeObserverRef.current?.disconnect();
  }, [
    rows,
    selectedRowIdSet,
    companyColors,
    imageAsset,
    manualBands,
    selectedVertices,
    drawPreview,
  ]);

  useEffect(() => {
    setReady(false);
  }, [imageAsset?.previewUrl]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (
        (event.key === 'Delete' || event.key === 'Backspace') &&
        selectedBand
      ) {
        event.preventDefault();
        onDeleteSelected?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedBand, onDeleteSelected]);

  const toNormalized = (event: React.MouseEvent) => {
    const canvasElement = canvasRef.current!;
    const rect = canvasElement.getBoundingClientRect();
    return {
      x: (event.clientX - rect.left) / rect.width,
      y: (event.clientY - rect.top) / rect.height,
    };
  };

  const hitTestBand = (nx: number, ny: number): SelectedBand | null => {
    for (let i = manualBands.length - 1; i >= 0; i -= 1) {
      const band = manualBands[i];
      if (band.vertices && pointInPolygon(band.vertices, nx, ny)) {
        return { type: 'manual', id: band.id };
      }
    }
    for (let i = rows.length - 1; i >= 0; i -= 1) {
      const row = rows[i];
      if (!selectedRowIdSet.has(row.rowId)) continue;
      if (isPointInsideRowBounds(row, nx, ny)) {
        return { type: 'ocr', id: row.rowId };
      }
    }
    return null;
  };

  const handleMouseDown = (event: React.MouseEvent) => {
    const p = toNormalized(event);
    if (tool === 'draw') {
      dragRef.current = { mode: 'draw', start: p };
      setDrawPreview(makeBandFromDrag(p.x, p.y, p.x, p.y, thickness));
      return;
    }
    if (tool === 'edit') {
      const hit = hitTestBand(p.x, p.y);
      onSelectBand?.(hit);
      dragRef.current = hit ? { mode: 'move', last: p, band: hit } : null;
    }
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    const p = toNormalized(event);
    if (drag.mode === 'draw' && drag.start) {
      setDrawPreview(
        makeBandFromDrag(drag.start.x, drag.start.y, p.x, p.y, thickness),
      );
    } else if (drag.mode === 'move' && drag.last && drag.band) {
      const dx = p.x - drag.last.x;
      const dy = p.y - drag.last.y;
      drag.last = p;
      if (dx || dy) onMoveBand?.(drag.band, dx, dy);
    }
  };

  const endDrag = () => {
    const drag = dragRef.current;
    if (drag?.mode === 'draw' && drawPreview) {
      onDrawBand?.(drawPreview);
    }
    dragRef.current = null;
    setDrawPreview(null);
  };

  const handleClick = (event: React.MouseEvent) => {
    if (tool !== 'select') return;
    const p = toNormalized(event);
    const hitRow = [...rows]
      .reverse()
      .find((row) => isPointInsideRowBounds(row, p.x, p.y));
    if (hitRow) onToggleRow(hitRow.rowId);
  };

  if (!imageAsset) {
    return (
      <div className="flex min-h-40 w-full items-center justify-center text-sm text-muted-foreground">
        처방 이미지를 불러오면 여기에서 하이라이트를 볼 수 있습니다.
      </div>
    );
  }

  const cursor =
    tool === 'draw' ? 'crosshair' : tool === 'edit' ? 'move' : 'pointer';

  return (
    <div className="flex w-full min-h-0">
      <div className="relative inline-block max-w-full leading-none">
        <img
          alt={imageAsset.name || 'prescription'}
          className="block h-auto max-w-full select-none"
          ref={imageRef}
          src={imageAsset.previewUrl}
          crossOrigin="anonymous"
          onLoad={() => setReady(true)}
        />
        <canvas
          ref={canvasRef}
          className="absolute left-0 top-0 touch-none"
          style={{ cursor, opacity: ready ? 1 : 0 }}
          onClick={handleClick}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={endDrag}
          onMouseLeave={endDrag}
        />
      </div>
    </div>
  );
};
